import { AssistantCitation } from '@services/modules/AssistantStream'

/*
 * The records an answer names, as the server pointed at them: every tool result lists the records
 * it read, each with a ref token ({{record:<id>}}), and the answer writes the token where it names
 * the record. The token is shown as that record's name, linked to that very record — which record
 * a name stands for is never worked out from the text. A token for a record the answer's citations
 * do not hold names nothing and is left out.
 */

const RECORD_TOKEN =
  /\{\{record:([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\}\}/g

/** Marks a token in the text handed to markdown: private-use characters markdown leaves alone. */
const MARK_START = String.fromCharCode(0xe000)
const MARK_END = String.fromCharCode(0xe001)
export const RECORD_MARK = new RegExp(
  `${MARK_START}([0-9a-fA-F-]{36})${MARK_END}`,
  'g'
)

/** The ids of the records a text names by token. */
export const recordTokenIds = (text: string | undefined): Set<string> => {
  const ids = new Set<string>()
  for (const match of (text ?? '').matchAll(RECORD_TOKEN)) {
    ids.add(match[1].toLowerCase())
  }
  return ids
}

/** The citations of an answer by id, for its tokens. */
export const citationsById = (
  citations: AssistantCitation[] | undefined
): Map<string, AssistantCitation> =>
  new Map((citations ?? []).map(c => [String(c.id).toLowerCase(), c]))

/**
 * The text for markdown: each token of a record the citations hold as a mark the markdown plugin
 * turns into that record's name (see remarkCitationLinks); any other token left out.
 */
export const markRecordTokens = (
  text: string,
  citations: AssistantCitation[] | undefined
): string => {
  const byId = citationsById(citations)
  return text.replace(RECORD_TOKEN, (_, id: string) =>
    byId.get(id.toLowerCase())?.label
      ? `${MARK_START}${id.toLowerCase()}${MARK_END}`
      : ''
  )
}

/** The text as plain words (Copy): each token as the record's name. */
export const recordTokensAsText = (
  text: string,
  citations: AssistantCitation[] | undefined
): string => {
  const byId = citationsById(citations)
  return text.replace(
    RECORD_TOKEN,
    (_, id: string) => byId.get(id.toLowerCase())?.label ?? ''
  )
}
